import { useState, useEffect, useCallback } from 'react';
import { getProcessedSleeperData } from '@/utils/sleeperIntegration';
import { filterPlayersWithSummary } from '@/utils/playerValue';
import { adjustProjectionsForScoring } from '@/utils/sleeperRosterIntegration';
import { playerImageUrl } from '@/utils/playerAssets';
import { displayDstName } from '@/utils/teams';
import { nflSeasonContext } from '@/utils/nflSeason';
import { finalizeRanks } from '@/utils/valuation/marketAlignment';
import type { Player } from '@/types/player';
import type { LeagueSettings as PlayerValueLeagueSettings } from '@/utils/playerEvaluation';
import type { LeagueSettings as TradeLeagueSettings } from '@/types/trade';



export const usePlayerData = (
  activeLeagueSettings: PlayerValueLeagueSettings,
  hasImportedSettings: boolean,
  leagueSettings?: TradeLeagueSettings
) => {
  const [playersPool, setPlayersPool] = useState<Player[]>([]);
  const [isLoadingPlayers, setIsLoadingPlayers] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Calculate market alignment percentage
  const calculateMarketAlignment = useCallback((counts: Record<string, number>) => {
    const target = { QB: 1, RB: 10, WR: 9, TE: 3, K: 1 };
    let matches = 0;
    let total = 0;
    
    Object.entries(target).forEach(([pos, targetCount]) => {
      const actual = counts[pos] || 0;
      matches += Math.min(actual, targetCount);
      total += targetCount;
    });
    
    return (matches / total) * 100;
  }, []);

  // Recalculate player values when league settings change
  const recalculatePlayerValues = useCallback((
    players: Player[], 
    _leagueSettings: PlayerValueLeagueSettings, 
    tradeLeagueSettings?: TradeLeagueSettings
  ) => {
    let adjustedPlayers = players;

    // First, adjust projections if we have trade league settings (with scoring rules)
    if (tradeLeagueSettings) {
      adjustedPlayers = adjustProjectionsForScoring(players, tradeLeagueSettings);
    }

    // Apply market alignment finalizer for proper positional distribution
    const result = finalizeRanks(adjustedPlayers);
    
    return result.full;
  }, []);

  useEffect(() => {
    // Prevent multiple simultaneous fetches
    if (hasInitialized || isLoadingPlayers) return;

    async function fetchPlayers() {
      console.log('MyRoster: Starting player fetch');
      setIsLoadingPlayers(true);
      setHasInitialized(true);
      
      try {
        const { lastCompleted } = nflSeasonContext();
        const { 
          statsMap, 
          projections: projectionsMap, 
          playerStatsMap, 
          teamUsageMap, 
          teamStatsMap, 
          leagueAverages 
        } = await getProcessedSleeperData(lastCompleted);

        const sleeperRes = await fetch('https://api.sleeper.app/v1/players/nfl');
        if (!sleeperRes.ok) throw new Error('Failed to fetch Sleeper data');
        const sleeperData = await sleeperRes.json();
        const playersArray = Object.values(sleeperData);

        const validPlayers = filterPlayersWithSummary(playersArray as any[], 'MyRoster', true);

        const mapped = validPlayers.map((p: any) => {
          const id = p.player_id;
          const position = p.position as 'QB' | 'RB' | 'WR' | 'TE' | 'K' | 'DEF';
          const team = p.team || '';
          const stats = statsMap.get(id) || ({} as any);
          const projection = projectionsMap[id] || 0;
          const games = stats.games_played ?? stats.gp ?? 1;
          const previousPPG = (stats.fpts_ppr ?? stats.fpts_half_ppr ?? stats.fpts_std ?? 0) / games;
          const playerStats = playerStatsMap.get(id) || { 
            targets: 0, carries: 0, redZoneOpportunities: 0, airYards: 0, schedule: [] 
          };
          const playerTeamStats = teamUsageMap.get(team) || { 
            targets: 0, carries: 0, redZoneOpportunities: 0, airYards: 0, schedule: [] 
          };
          const teamStats = teamStatsMap.get(team) || { 
            playsPerGame: 65, pointsPerGame: 23, redZoneScoreRate: 0.58, offensiveLineRank: 16 
          };
          const age = p.age || 27;
          const playerName = position === 'DEF' ? displayDstName(team) : (p.full_name || 'Unknown Player');
          
          const espnIdFromSleeper = p.espn_id ? String(p.espn_id) : undefined;
          const imageUrl = playerImageUrl(playerName, position, team, espnIdFromSleeper, id);

          return {
            id,
            name: playerName,
            position,
            team,
            byeWeek: p.bye_week || 0,
            adp: p.adp || 999,
            projectedPoints: projection,
            rank: 0,
            change: 0,
            imageUrl,
            projection,
            age,
            lastYearPoints: previousPPG * games,
            teamStats,
            leagueAverages,
            playerStats,
            playerTeamStats,
            injuryHistory: [],
            value: 0,
            vorp: 0,
            isActive: p.active !== false,
            lastUpdated: new Date().toISOString()
          } as Player;
        });

        // Calculate scores with current league settings using finalizer
        const result = finalizeRanks(mapped);
        const scored = result.full;

        // Debug: Log market alignment results
        console.log('MyRoster: Top 24 position distribution:', result.top24Counts);
        console.log('MyRoster: Market alignment target: QB:1, RB:10, WR:9, TE:3, K:1');
        
        const marketAlignment = calculateMarketAlignment(result.top24Counts);
        console.log('MyRoster: Market alignment score:', marketAlignment.toFixed(1) + '%');
        
        console.log('MyRoster: Top 10 players:', result.top24.slice(0, 10).map(p => 
          `${p.name} (${p.position}) - VORP: ${p.vorp?.toFixed(1)}, Value: ${p.value?.toFixed(1)}`
        ));
        
        setPlayersPool(scored);
        console.log('MyRoster: Successfully loaded', scored.length, 'players');
      } catch (e) {
        console.error('Error fetching players:', e);
        // Reset flags on error so user can retry
        setHasInitialized(false);
      } finally {
        setIsLoadingPlayers(false);
      }
    }

    fetchPlayers();
  }, [activeLeagueSettings, recalculatePlayerValues, leagueSettings, hasImportedSettings]);

  // Recalculate player values when league settings change
  useEffect(() => {
    if (playersPool.length > 0) {
      console.log('Recalculating player values due to league settings change...');
      const recalculatedPlayers = recalculatePlayerValues(
        playersPool, 
        activeLeagueSettings, 
        hasImportedSettings ? leagueSettings : undefined
      );
      setPlayersPool(recalculatedPlayers);
    }
  }, [activeLeagueSettings, leagueSettings, hasImportedSettings, recalculatePlayerValues]);

  return {
    playersPool,
    isLoadingPlayers,
    recalculatePlayerValues
  };
};